<template>
  <!--
    Studio · Profile · Avatar picker. Full-panel takeover (NOT a
    modal) — rendered when ProfilePanel's view flips to 'avatar'.
    Three tabs:

      Browse   → public portrait pool, hierarchical filter, click →
                 confirm → save + back to Profile.
      Simple   → 1-line summary of what we already know about the
                 reader (illustration_theme + illustration_instructions)
                 + textarea for "anything to change?" + Paint triptych.
                 Pre-populates from Reader fields at mount.
      Advanced → 5-section editable accordion, pre-populated from
                 Reader.illustration_instructions JSON when present.
                 Generates a triptych, click one to save.

    Discard contract: every gen result lands in ThemePortraitPool
    with is_claimed=False. Picking one flips that one to claimed via
    set-avatar-from-url's pool_id param; unpicked siblings stay
    browsable for everyone.
  -->
  <div class="avatar-picker">
    <!-- Inside the Studio modal the modal owns the title and the close, so the
         header keeps only what is its own: the portrait help and the archive. -->
    <header class="picker-head" :class="{ embedded }">
      <button v-if="!embedded" class="back-btn" @click="$emit('close')">← Back to Profile</button>
      <h2 class="picker-title">
        <template v-if="!embedded">Choose your avatar</template>
        <FieldHelpIcon
          field="portrait_help"
          label="Portrait"
          panel="profile"
          :value="''"
          title="Ask Mystic Aida for help with your portrait"
        />
      </h2>
      <button
        class="archive-btn"
        :disabled="!archive.length"
        :title="archive.length ? 'View past portraits' : 'No past portraits yet - paint one first'"
        @click="showArchiveModal = true"
      >
        Archive{{ archive.length ? ` (${archive.length})` : '' }}
      </button>
    </header>

    <div class="tab-bar">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'simple' }"
        @click="switchToSimple"
      >Simple</button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'advanced' }"
        @click="switchToAdvanced"
      >Advanced</button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'browse' }"
        @click="switchToBrowse"
      >Browse</button>
    </div>

    <!-- ===================== BROWSE ===================== -->
    <div v-if="activeTab === 'browse'" class="browse-tab">
      <div v-if="filterLevel > 0" class="breadcrumb">
        <button class="crumb-back" @click="goBack">← Back</button>
        <span class="crumb-trail">
          <span v-if="filters.era" class="crumb-item" @click="resetToLevel(1)">{{ filters.era }}</span>
          <span v-if="filters.theme" class="crumb-item" @click="resetToLevel(2)"> › {{ filters.theme }}</span>
          <span v-if="filters.subject_type" class="crumb-item" @click="resetToLevel(3)"> › {{ filters.subject_type }}</span>
          <span v-if="filters.gender" class="crumb-item"> › {{ filters.gender }}</span>
        </span>
      </div>
      <div class="filter-pills">
        <template v-if="filterLevel === 0">
          <button v-for="e in availableEras" :key="e.value" class="pill" @click="pickEra(e.value)">{{ e.label }}</button>
        </template>
        <template v-else-if="filterLevel === 1">
          <button v-for="t in availableThemes" :key="t.value" class="pill" @click="pickTheme(t.value)">{{ t.label }}</button>
        </template>
        <template v-else-if="filterLevel === 2">
          <button v-for="s in availableSubjects" :key="s.value" class="pill" @click="pickSubject(s.value)">{{ s.label }}</button>
        </template>
        <template v-else-if="filterLevel === 3 && (filters.subject_type === 'human' || filters.subject_type === 'mythical')">
          <button v-for="g in availableGenders" :key="g.value" class="pill" @click="pickGender(g.value)">{{ g.label }}</button>
        </template>
      </div>

      <div v-if="loading" class="status-msg">Loading…</div>
      <div v-else-if="!portraits.length" class="status-msg">
        No portraits found for this combination - try a different
        path, or switch to Simple/Advanced to generate one.
      </div>
      <div v-else class="portrait-grid">
        <button
          v-for="p in portraits"
          :key="p.id"
          class="portrait-card"
          :class="{ saving: savingPoolId === p.id }"
          :disabled="claiming"
          @click="openBrowsePreview(p)"
        >
          <img :src="p.portrait_url" :alt="p.theme" class="portrait-img" loading="lazy" />
          <span v-if="savingPoolId === p.id" class="card-saving-overlay">Saving…</span>
        </button>
        <div v-if="hasMore" ref="scrollSentinel" class="scroll-sentinel"></div>
      </div>
      <div v-if="loadingMore" class="loading-more">Loading…</div>

      <!-- Browse preview modal — clicking a portrait card opens this
           instead of a window.confirm(). Shows the full-size image
           plus Cancel / Choose Avatar so the operator can see what
           they're picking before committing. -->
      <div
        v-if="previewPortrait"
        class="preview-modal-backdrop"
        @click.self="closeBrowsePreview"
      >
        <div class="preview-modal-card">
          <button
            class="preview-modal-close"
            aria-label="Close preview"
            @click="closeBrowsePreview"
          >×</button>
          <div class="preview-modal-image-wrap">
            <img
              :src="previewPortrait.portrait_url"
              :alt="previewPortrait.theme || 'portrait preview'"
              class="preview-modal-image"
            />
          </div>
          <footer class="preview-modal-foot">
            <button
              class="ghost-btn"
              :disabled="claiming"
              @click="closeBrowsePreview"
            >Cancel</button>
            <button
              class="primary-btn"
              :disabled="claiming"
              @click="chooseBrowsePreview"
            >{{ claiming ? 'Saving…' : 'Choose Avatar →' }}</button>
          </footer>
        </div>
      </div>
    </div>

    <!-- ===================== SIMPLE ===================== -->
    <div v-else-if="activeTab === 'simple'" class="simple-tab">
      <div class="known-card" :class="{ overridden: !!simpleTweak.trim() }">
        <div class="known-label">
          {{ simpleTweak.trim() ? 'New direction (from your change)' : 'What I already know about your reader' }}
        </div>
        <p class="known-summary">
          {{ simpleTweak.trim() ? simpleTweak.trim() : readerKnownSummary }}
        </p>
      </div>

      <label class="tweak-label">
        <span>
          Anything you want to change?
          <FieldHelpIcon
            field="portrait_simple_tweak"
            label="Portrait change"
            panel="profile"
            :value="simpleTweak"
            title="Let Mystic Aida shape this with you"
          />
        </span>
        <small>Optional. Leave blank to paint with what's above.</small>
      </label>
      <textarea
        v-model="simpleTweak"
        class="tweak-textarea"
        rows="3"
        placeholder="e.g. lighter palette, no animals, more cosmic..."
      ></textarea>

      <PaintingStatus
        v-if="generating"
        :elapsed="paintElapsed"
        :typical="PAINT_TYPICAL_SECONDS"
      />
      <div v-else-if="!triptych.length" class="paint-bar">
        <button
          class="paint-btn"
          @click="paintTriptych"
        >🎨 Paint portrait</button>
      </div>

      <TriptychResult
        v-if="triptych.length"
        :portraits="triptych"
        :saving-pool-id="savingPoolId"
        :claiming="claiming"
        :compiled-prompt="compiledDescription"
        @pick="confirmAndUseTriptychPortrait"
        @refine="resetTriptych"
      />
    </div>

    <!-- ===================== ADVANCED ===================== -->
    <div v-else class="advanced-tab">
      <VisualVoiceEditor
        v-model="desc"
        field-prefix="portrait"
        panel="profile"
        :initial-active-section="activeSection"
        @section-focus="activeSection = $event"
        ref="visualVoiceEditorRef"
      />

      <PaintingStatus
        v-if="generating"
        :elapsed="paintElapsed"
        :typical="PAINT_TYPICAL_SECONDS"
      />
      <div v-else-if="!triptych.length" class="paint-bar">
        <button class="view-prompt-btn" @click="showPromptModal = true">
          View Prompt
        </button>
        <button
          class="paint-btn"
          @click="paintTriptych"
        >🎨 Paint portrait</button>
      </div>

      <TriptychResult
        v-if="triptych.length"
        :portraits="triptych"
        :saving-pool-id="savingPoolId"
        :claiming="claiming"
        :compiled-prompt="compiledDescription"
        @pick="confirmAndUseTriptychPortrait"
        @refine="resetTriptych"
      />

      <div
        v-if="showPromptModal"
        class="prompt-modal-overlay"
        @click.self="showPromptModal = false"
      >
        <div class="prompt-modal">
          <div class="prompt-modal-header">
            <span>Full Illustrative Prompt</span>
            <button class="prompt-modal-close" @click="showPromptModal = false">×</button>
          </div>
          <div class="prompt-modal-body">{{ compiledDescription }}</div>
        </div>
      </div>
    </div>

    <p v-if="rewriteNotice" class="rewrite-notice">
      ✨ Aida rewrote your prompt to comply with the image model's
      content policy (probably a trademarked name). The portrait
      below uses the descriptive equivalent.
    </p>
    <p v-if="error" class="error">⚠ {{ error }}</p>

    <!-- Archive modal — every successful Paint click is appended to
         localStorage so the operator can revisit past gens and pick
         one they didn't originally save. Stored per reader so a
         second mystic on the same browser doesn't see someone
         else's history. Capped at 50 entries (FIFO). -->
    <div
      v-if="showArchiveModal"
      class="archive-modal-backdrop"
      @click.self="showArchiveModal = false"
    >
      <div class="archive-modal-card">
        <header class="archive-modal-head">
          <h3>Past portraits</h3>
          <button
            class="archive-modal-close"
            @click="showArchiveModal = false"
            aria-label="Close archive"
          >×</button>
        </header>
        <div v-if="!archive.length" class="archive-empty">
          No archived portraits yet - every Paint shows up here so
          you can revisit one you didn't originally pick.
        </div>
        <div v-else class="archive-grid">
          <button
            v-for="entry in archive"
            :key="entry.ts + ':' + entry.portrait_url"
            class="archive-cell"
            @click="openArchiveEntry(entry)"
            :title="entry.prompt"
          >
            <img :src="entry.portrait_url" :alt="entry.prompt" loading="lazy" />
            <span class="archive-meta">{{ formatTs(entry.ts) }}</span>
          </button>
        </div>
        <footer class="archive-modal-foot">
          <button
            v-if="archive.length"
            class="ghost-btn-sm"
            @click="clearArchive"
          >Clear archive</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup>
defineProps({
  // Rendered inside StudioEditModal, which owns the title and the close.
  embedded: { type: Boolean, default: false },
})
import { ref, reactive, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { store } from '@/store'
import {
  browseStudioPortraits,
  generateStudioPortrait,
  setReaderAvatarFromUrl,
} from '@/api-service'
import {
  dispatchFieldContext, studioAssistantState,
} from '@/utils/studio-assistant'
import TriptychResult from './TriptychResult.vue'
import PaintingStatus from './PaintingStatus.vue'
import FieldHelpIcon from './FieldHelpIcon.vue'
import VisualVoiceEditor from './VisualVoiceEditor.vue'


const emit = defineEmits(['close'])

// ─── Snippet templates (mirror ImageStep — used as fallbacks
// when Reader.illustration_instructions is missing/unparseable). ──
const ERA_SNIPPETS = {
  classic: 'A classic mystical portrait, rich oil-painted textures, ancient timeless aesthetic.',
  modern: 'A modern mystical portrait, clean digital art, contemporary ethereal feel.',
  futuristic: 'A futuristic mystical portrait, cosmic energy, neon accents, otherworldly technology.',
}
const ENVIRONMENT_SNIPPETS = {
  forest: 'Set in an ancient forest with warm lantern light, mist between trees, moss-covered stones.',
  coastal: 'Set on a coastal cliff, sea mist, tide pools reflecting moonlight, crashing waves.',
  desert: 'Set in sand dunes at night, campfire glow, star field above, sandstone arches.',
  garden: 'Set in an enchanted garden, flowering vines, stone fountain, fireflies, twilight glow.',
  mountain: 'Set on a mountain summit, alpine meadow, thin clouds, sunrise light.',
  underwater: 'Set in the deep ocean, bioluminescent light, coral formations, floating jellyfish.',
  volcanic: 'Set in a volcanic caldera, flowing lava rivers, obsidian formations, ember-lit smoke.',
  arctic: 'Set in the frozen tundra, aurora borealis, ice crystals, silver moonlight on snow.',
  jungle: 'Set in dense jungle canopy, ancient vines, exotic birds, filtered sunlight, waterfall.',
  meadow: 'Set in a wildflower meadow at golden hour, butterflies, gentle breeze, distant hills.',
  library: 'Set in a candlelit study, towering bookshelves, antique maps, warm firelight.',
  temple: 'Set in an ancient stone temple, incense smoke, carved pillars, sacred geometry, dawn light.',
  city: 'Set on an urban rooftop at dusk, city skyline, warm ambient light, string lights.',
  ruins: 'Set in overgrown ruins, crumbling columns, ivy and wildflowers reclaiming stone.',
  observatory: 'Set in an observatory dome, brass telescope, star charts on walls, night sky through open roof.',
  apothecary: 'Set in a cluttered apothecary, glass jars with herbs, mortar and pestle, candlelight.',
  throne_room: 'Set in a grand throne room, tapestries, stained glass, flickering torchlight.',
  celestial: 'Set on the celestial plane, floating among stars, moon halo, cosmic dust.',
  void: 'Set in the void, abstract darkness, faint geometric light, echo of stars.',
  aurora: 'Set beneath the aurora borealis, snow-dusted peaks, prismatic light.',
  eclipse: 'Set during a solar eclipse, corona of light, shadow and brilliance.',
  nebula: 'Set inside a nebula, gas clouds of pink and purple, newborn stars.',
}
const SUBJECT_SNIPPETS = {
  human: 'The subject is a human mystic, visible face and upper body, facing the viewer.',
  creature: 'The subject is a mystical creature - full form visible, centered composition.',
  mythical: 'The subject is a mythical being - elven, fae, or otherworldly, visible face, facing viewer.',
  object: 'The subject is a mystical object or symbol - centered, luminous, intricate detail.',
}
const GENDER_SNIPPETS = {
  male: 'Male-presenting, with strong defined features.',
  female: 'Female-presenting, with graceful elegant features.',
  neutral: 'Androgynous, with ambiguous ethereal features.',
}
const MOOD_SNIPPETS = {
  serene: 'Serene mood - peaceful, still, soft light, gentle expression.',
  fierce: 'Fierce mood - intense gaze, dramatic lighting, bold stance.',
  playful: 'Playful mood - mischievous energy, whimsical elements.',
  mysterious: 'Mysterious mood - half-shadowed, enigmatic, secrets in the eyes.',
}
const PALETTE_SNIPPETS = {
  warm_gold: 'Color palette: warm golds and ambers.',
}
const STYLE_SNIPPETS = {
  oil_painting: 'Style: rich oil painting, visible brushstrokes, classical composition.',
}

const eraOptions = [
  { value: 'classic', label: 'Classic' },
  { value: 'modern', label: 'Modern' },
  { value: 'futuristic', label: 'Futuristic' },
]
const allEnvironments = Object.keys(ENVIRONMENT_SNIPPETS).map(k => ({
  value: k,
  label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
}))
const subjectOptions = [
  { value: 'human', label: 'Human' },
  { value: 'creature', label: 'Creatures' },
  { value: 'mythical', label: 'Mythical' },
  { value: 'object', label: 'Objects' },
]
const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'neutral', label: 'Neutral' },
]

// ─── Tabs / shared state ────────────────────────────────────────
const activeTab = ref('simple')
const error = ref('')
const claiming = ref(false)
// Single-portrait gen by default. Cheaper + faster, and the
// preview-then-choose modal makes "spin again" a one-click action
// for refines. Triptych logic still works on the BE if we ever
// want to re-enable it.
const count = 1

function switchToBrowse() {
  activeTab.value = 'browse'
  // Browse is purely visual — close the AI assistant column so the
  // gallery has the full panel width. Simple/Advanced keep Aida
  // available since they involve free-form prompt shaping.
  window.dispatchEvent(new CustomEvent('studio-assistant:close'))
}
// Open Aida with portrait_help context so messages route through
// the studio/field-polish endpoint (lands in the Simple tweak box)
// instead of the keyword-based STUDIO_NAV_MAP fallback. If the
// operator is already on a more-specific portrait_* field (e.g.
// they clicked the Mood section's ?), keep that context — only
// seed portrait_help when no field is active.
function ensurePortraitChatContext() {
  const active = studioAssistantState.activeField
  const isPortraitField = active && (
    active.field === 'portrait_help'
    || active.field === 'portrait_simple_tweak'
    || (active.field || '').startsWith('portrait_')
  )
  if (isPortraitField) {
    // Already scoped to portrait — just make sure the column is up.
    window.dispatchEvent(new CustomEvent('studio-assistant:open'))
  } else {
    // Set portrait_help as the default field. ``source: 'click'``
    // force-opens the panel + seeds the static opener.
    dispatchFieldContext({
      field: 'portrait_help',
      label: 'Portrait',
      panel: 'profile',
      value: '',
      source: 'click',
    })
  }
}

function switchToSimple() {
  activeTab.value = 'simple'
  ensurePortraitChatContext()
}
function switchToAdvanced() {
  activeTab.value = 'advanced'
  ensurePortraitChatContext()
  if (filters.era && !desc.eraText) {
    desc.era = filters.era
    desc.eraText = ERA_SNIPPETS[filters.era] || ''
  }
  if (filters.theme && !desc.environmentText) {
    desc.environment = filters.theme
    desc.environmentText = ENVIRONMENT_SNIPPETS[filters.theme] || ''
  }
  if (filters.subject_type) {
    desc.subjectType = filters.subject_type
    desc.subjectText = SUBJECT_SNIPPETS[filters.subject_type] || ''
  }
}

// ─── Browse state ───────────────────────────────────────────────
const portraits = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const hasMore = ref(false)
const savingPoolId = ref(null)
const page = ref(1)
const scrollSentinel = ref(null)
let observer = null

const filterLevel = ref(0)
const filters = reactive({ era: '', theme: '', subject_type: '', gender: '' })
const availableData = ref({ eras: {}, themes: {}, subject_types: {}, genders: {} })

const availableEras = computed(() =>
  eraOptions.filter(o => availableData.value.eras[o.value]),
)
const availableThemes = computed(() =>
  allEnvironments.filter(o => availableData.value.themes[o.value]),
)
const availableSubjects = computed(() =>
  subjectOptions.filter(o => availableData.value.subject_types[o.value]),
)
const availableGenders = computed(() =>
  genderOptions.filter(o => availableData.value.genders[o.value]),
)

function pickEra(v) { filters.era = v; filterLevel.value = 1; desc.era = v; desc.eraText = ERA_SNIPPETS[v] || ''; fetchFiltered() }
function pickTheme(v) { filters.theme = v; filterLevel.value = 2; desc.environment = v; desc.environmentText = ENVIRONMENT_SNIPPETS[v] || ''; fetchFiltered() }
function pickSubject(v) {
  filters.subject_type = v
  desc.subjectType = v
  desc.subjectText = SUBJECT_SNIPPETS[v] || ''
  filterLevel.value = (v === 'human' || v === 'mythical') ? 3 : 4
  fetchFiltered()
}
function pickGender(v) { filters.gender = v; filterLevel.value = 4; fetchFiltered() }

function goBack() {
  if (filterLevel.value <= 0) return
  if (filterLevel.value === 1) filters.era = ''
  else if (filterLevel.value === 2) filters.theme = ''
  else if (filterLevel.value === 3) filters.subject_type = ''
  else if (filterLevel.value === 4) { filters.gender = ''; filters.subject_type = '' }
  filterLevel.value = Math.max(0, filterLevel.value - 1)
  fetchFiltered()
}
function resetToLevel(level) {
  if (level <= 1) { filters.theme = ''; filters.subject_type = ''; filters.gender = '' }
  if (level <= 2) { filters.subject_type = ''; filters.gender = '' }
  if (level <= 3) { filters.gender = '' }
  filterLevel.value = level
  fetchFiltered()
}

function fetchFiltered() {
  page.value = 1
  portraits.value = []
  fetchPortraits()
}

async function fetchPortraits() {
  if (page.value === 1) loading.value = true
  try {
    const data = await browseStudioPortraits(filters, page.value, 9)
    if (page.value === 1) portraits.value = data.portraits || []
    else portraits.value.push(...(data.portraits || []))
    hasMore.value = !!data.has_more
    if (data.available) availableData.value = data.available
    nextTick(() => setupObserver())
  } catch (e) {
    error.value = e?.response?.data?.error || String(e)
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

function setupObserver() {
  if (observer) observer.disconnect()
  if (!scrollSentinel.value) return
  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && hasMore.value && !loading.value && !loadingMore.value) {
      loadingMore.value = true
      page.value++
      fetchPortraits()
    }
  }, { threshold: 0.1 })
  observer.observe(scrollSentinel.value)
}
onUnmounted(() => { if (observer) observer.disconnect() })

// Browse-tab preview modal. Clicking a portrait card opens the
// preview with a large image + Cancel/Choose Avatar buttons.
// Choosing actually saves; cancelling just closes the modal.
const previewPortrait = ref(null)

function openBrowsePreview(p) {
  if (claiming.value) return
  previewPortrait.value = p
}
function closeBrowsePreview() {
  if (claiming.value) return
  previewPortrait.value = null
}
async function chooseBrowsePreview() {
  const p = previewPortrait.value
  if (!p || claiming.value) return
  savingPoolId.value = p.id
  await applyAvatarUrl(p.portrait_url, p.id)
  savingPoolId.value = null
  if (!error.value) {
    previewPortrait.value = null
    emit('close')
  }
}

// ─── Advanced/Simple shared description state ──────────────────
// Pre-populated from Reader.illustration_instructions on mount so
// the Mystic walks in with their existing visual context already
// captured — they refine instead of starting from scratch.
const desc = reactive({
  era: 'classic',
  eraText: ERA_SNIPPETS.classic + ' ' + STYLE_SNIPPETS.oil_painting,
  environment: '',
  environmentText: '',
  subjectType: 'human',
  subjectText: SUBJECT_SNIPPETS.human + ' ' + GENDER_SNIPPETS.neutral,
  moodText: MOOD_SNIPPETS.mysterious + ' ' + PALETTE_SNIPPETS.warm_gold,
  extraText: '',
})
const simpleTweak = ref('')

const compiledDescription = computed(() => {
  const tweak = simpleTweak.value.trim()
  // Simple mode + a tweak = the tweak IS the prompt. No reader
  // context appended — the BE wrapper already adds "Portrait of a
  // spiritual reader named X. Square composition." for grounding,
  // and any era/style/mood mentioned in the tweak comes through
  // the polish endpoint already. Mixing in eraText/moodText again
  // double-weighted the saved coastal/etc context and drowned out
  // the new direction (e.g. "mermaid scene" became a side-note
  // inside a coastal painting instead of THE scene).
  if (activeTab.value === 'simple' && tweak) {
    // BE wrapper adds "No text. No logos. Square composition."
    return tweak
  }
  // Advanced (or Simple with no tweak) — use the full accordion.
  return [
    desc.eraText, desc.environmentText, desc.subjectText,
    desc.moodText, desc.extraText,
  ].filter(Boolean).join(' ')
})

// Section list lives in VisualVoiceEditor now; AvatarPicker only
// tracks which one is currently expanded so the assistant's polish
// payload knows where to land.
const activeSection = ref('era')
const visualVoiceEditorRef = ref(null)
const generating = ref(false)
const triptych = ref([])
const showPromptModal = ref(false)
// True after a content-policy rejection forced Aida to rewrite the
// prompt and try again successfully. Cleared on the next paint.
const rewriteNotice = ref(false)
// Elapsed-seconds counter during painting. Mirrors the Custom Deck
// CardLightbox status indicator so the operator has a sense of
// progress on a 30-90s LLM call.
const paintElapsed = ref(0)
let paintTimer = null
// gpt-image-2 at low quality, single image. Launder-retry can
// roughly double this when the first attempt hits content policy.
const PAINT_TYPICAL_SECONDS = 30

// ─── Archive (localStorage-backed history of past gens) ─────────
// Keyed per-reader so a second Mystic on the same browser doesn't
// see someone else's portraits. Each entry: {portrait_url, pool_id,
// prompt, ts}. Cap at 50 (FIFO) so storage doesn't bloat.
const ARCHIVE_CAP = 50
const archive = ref([])
const showArchiveModal = ref(false)

function archiveStorageKey() {
  const rid = store.reader?.id || store.reader?.sub_domain || 'default'
  return `studio:avatar:archive:${rid}`
}
function loadArchive() {
  try {
    const raw = localStorage.getItem(archiveStorageKey())
    archive.value = raw ? JSON.parse(raw) : []
  } catch {
    archive.value = []
  }
}
function persistArchive() {
  try {
    localStorage.setItem(
      archiveStorageKey(), JSON.stringify(archive.value),
    )
  } catch { /* quota or private mode — silent */ }
}
function appendToArchive(entries, prompt) {
  const ts = Date.now()
  const next = entries
    .filter(e => e?.portrait_url)
    .map(e => ({
      portrait_url: e.portrait_url,
      pool_id: e.pool_id || null,
      prompt: prompt || '',
      ts,
    }))
  // Newest first; cap at ARCHIVE_CAP
  archive.value = [...next, ...archive.value].slice(0, ARCHIVE_CAP)
  persistArchive()
}
function clearArchive() {
  archive.value = []
  persistArchive()
}
function formatTs(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}
function openArchiveEntry(entry) {
  // Reuse the preview modal — same Cancel / Choose Avatar flow.
  showArchiveModal.value = false
  previewPortrait.value = {
    id: entry.pool_id,
    portrait_url: entry.portrait_url,
  }
}

function startPaintTimer() {
  paintElapsed.value = 0
  if (paintTimer) clearInterval(paintTimer)
  paintTimer = setInterval(() => { paintElapsed.value += 1 }, 1000)
}
function stopPaintTimer() {
  if (paintTimer) { clearInterval(paintTimer); paintTimer = null }
}

// expandSection / onSectionBlur / onSectionFocus moved to
// VisualVoiceEditor — AvatarPicker only listens to @section-focus
// to keep its activeSection ref in sync.

// Pre-populate the accordion from Reader.illustration_instructions
// (the JSON the onboarding interview produced + Reader.illustration_theme).
// Each onboarding-derived field maps into one accordion section so
// the Mystic edits real captured context, not stock placeholders.
function hydrateFromReader() {
  const reader = store.reader || {}
  let inst = reader.illustration_instructions
  if (typeof inst === 'string') {
    try { inst = JSON.parse(inst) } catch { inst = null }
  }
  if (inst && typeof inst === 'object') {
    const eraBits = []
    if (inst.style) eraBits.push(inst.style)
    if (inst.composition) eraBits.push(inst.composition)
    if (inst.lighting) eraBits.push(inst.lighting)
    if (eraBits.length) desc.eraText = eraBits.join(' ')

    const envFromTheme = ENVIRONMENT_SNIPPETS[reader.illustration_theme]
    if (envFromTheme) {
      desc.environment = reader.illustration_theme
      desc.environmentText = envFromTheme
    }
    if (Array.isArray(inst.symbolic_objects) && inst.symbolic_objects.length) {
      desc.environmentText = (
        (desc.environmentText ? desc.environmentText + ' ' : '')
        + 'Featuring: ' + inst.symbolic_objects.join(', ') + '.'
      )
    }
    if (inst.subject) desc.subjectText = inst.subject
    const moodPalette = [inst.mood, inst.palette].filter(Boolean).join('. ')
    if (moodPalette) desc.moodText = moodPalette
    if (inst.negative_space) desc.extraText = inst.negative_space
  } else if (reader.illustration_theme && ENVIRONMENT_SNIPPETS[reader.illustration_theme]) {
    desc.environment = reader.illustration_theme
    desc.environmentText = ENVIRONMENT_SNIPPETS[reader.illustration_theme]
  }
}

// One-line summary of what's already captured — shown atop Simple.
const readerKnownSummary = computed(() => {
  const reader = store.reader || {}
  const bits = []
  if (reader.illustration_theme) bits.push(`a ${reader.illustration_theme} setting`)
  let inst = reader.illustration_instructions
  if (typeof inst === 'string') {
    try { inst = JSON.parse(inst) } catch { inst = null }
  }
  if (inst?.mood) bits.push(`${inst.mood} mood`)
  if (inst?.palette) bits.push(`${inst.palette} palette`)
  if (inst?.style) bits.push(`${inst.style} style`)
  if (!bits.length) {
    return "We don't have much yet - leave the box blank to start with a default celestial portrait, or describe what you'd like."
  }
  return `Your reader has ${bits.join(', ')}. I'll paint with that.`
})

// Translate axios / BE failures into user-facing copy. The default
// "timeout of 180000ms exceeded" / "Network Error" axios messages
// are not actionable for a Mystic — they need to know whether to
// retry, simplify, or rephrase.
function friendlyPaintError(e) {
  const code = e?.code
  const beMsg = e?.response?.data?.error || ''
  // BE-classified failures (more specific than HTTP errors)
  if (beMsg.startsWith('content_policy')) {
    return (
      "Mystic Aida couldn't paint that - your prompt was blocked by "
      + 'the image model\'s content policy (often a trademarked name). '
      + 'Try rewording without brand names or specific characters.'
    )
  }
  if (beMsg) {
    return `Painting failed: ${beMsg}`
  }
  // Axios-level failures
  if (code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) {
    return (
      'The painter is running slow right now - Aida didn\'t hear back '
      + 'in time. The image may still finish in the background; try '
      + 'painting again in a moment. If it keeps stalling, simplify '
      + 'the description (one main subject + one mood).'
    )
  }
  if (code === 'ERR_NETWORK' || /network/i.test(e?.message || '')) {
    return (
      'Lost connection while painting. Check your internet and try '
      + 'again - your last description is still in the box.'
    )
  }
  if (e?.response?.status === 502 || e?.response?.status === 504) {
    return (
      'The image service is having a moment. Try painting again in '
      + 'a few seconds.'
    )
  }
  if (e?.response?.status === 401) {
    return 'Your session expired - sign in again to keep painting.'
  }
  // Last-ditch fallback
  return (
    'Something went wrong while painting. Try once more, or refine '
    + 'your description to be a little simpler.'
  )
}

async function paintTriptych() {
  generating.value = true
  triptych.value = []
  error.value = ''
  rewriteNotice.value = false
  startPaintTimer()
  try {
    const r = await generateStudioPortrait(compiledDescription.value, count)
    triptych.value = r?.portraits || []
    if (!triptych.value.length) {
      // Surface the BE's specific failure reason if present (it now
      // returns "All gen attempts failed: gen: …" / "upload: …" /
      // "worker: …" / "content_policy: …" so we can see which step
      // blew up).
      error.value = r?.error
        || 'No portraits returned - try refining the description.'
    } else {
      // Show a notice if the BE had to launder + retry the prompt
      // due to a content-policy rejection (any portrait carries
      // ``rewritten_prompt`` when that happened).
      rewriteNotice.value = triptych.value.some(p => p.rewritten_prompt)
      // Persist into the per-reader Archive so the operator can
      // revisit any past gen they didn't originally pick.
      appendToArchive(triptych.value, compiledDescription.value)
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('paintTriptych failed', e)
    error.value = friendlyPaintError(e)
  } finally {
    generating.value = false
    stopPaintTimer()
  }
}

function resetTriptych() {
  triptych.value = []
}

// Triptych click reuses the Browse preview-modal pattern: clicking
// a generated portrait opens the larger-image modal with Cancel /
// Choose Avatar instead of an immediate save. The TriptychResult
// component still emits ``pick`` — we just route it into the
// preview state instead of straight to ``applyAvatarUrl``.
function confirmAndUseTriptychPortrait(p) {
  if (claiming.value) return
  // Wrap the triptych payload so the preview modal renders the
  // same shape it does for browse cards. ``pool_id`` carries
  // through so the picked row gets claimed; ``portrait_url`` is
  // the image source.
  previewPortrait.value = { id: p.pool_id, portrait_url: p.portrait_url }
}

// ─── Shared apply-and-save ──────────────────────────────────────
async function applyAvatarUrl(url, poolId = null) {
  claiming.value = true
  error.value = ''
  try {
    const r = await setReaderAvatarFromUrl(url, poolId)
    const finalUrl = r?.image_url || url
    if (store.reader) store.reader.background_image = finalUrl
  } catch (e) {
    error.value = e?.response?.data?.error || 'Could not save avatar.'
  } finally {
    claiming.value = false
  }
}

// When Aida polishes a portrait_* field via the studio chat, the
// polished value comes back as ``studio-assistant:polish-applied``.
// Route it into the matching local state so the accordion / tweak
// box shows it; the Mystic can review + tweak before painting.
function onPolishApplied(e) {
  const { field, value } = e.detail || {}
  if (!field || value === undefined) return
  // Top-level help icon ("portrait_help") and the Simple tab's
  // tweak icon both land in the Simple tweak textarea — that's the
  // free-form "anything to change" target. Aida's confirmation in
  // chat tells the operator we wrote it; switching to Simple makes
  // the landing visible.
  if (field === 'portrait_help' || field === 'portrait_simple_tweak') {
    simpleTweak.value = value
    activeTab.value = 'simple'
    return
  }
  // Per-section icons (era / environment / subject / mood / extra)
  // land in the matching accordion section. Switch to Advanced first
  // so the editor is mounted, then delegate to VisualVoiceEditor's
  // applyPolishedValue which handles the desc write + section
  // expansion + contenteditable refresh.
  if (field.startsWith('portrait_')) {
    activeTab.value = 'advanced'
    nextTick(() => {
      visualVoiceEditorRef.value?.applyPolishedValue(field, value)
    })
  }
}

onMounted(() => {
  hydrateFromReader()
  loadArchive()
  fetchPortraits()
  // Default tab is Simple → bring up Mystic AIda's assistant column so she's
  // ready to help shape the portrait from the start.
  ensurePortraitChatContext()
  window.addEventListener('studio-assistant:polish-applied', onPolishApplied)
})
onUnmounted(() => {
  window.removeEventListener('studio-assistant:polish-applied', onPolishApplied)
})
</script>

<style scoped>
.avatar-picker {
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: #E8D8FF;
}
.picker-head.embedded { justify-content: flex-end; gap: 10px; padding-top: 0; }
.picker-head.embedded .picker-title { flex: 0 0 auto; }
.picker-head {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(195, 169, 255, 0.12);
}
.back-btn {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.85);
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
}
.back-btn:hover { border-color: #C9A94E; color: #C9A94E; }
.picker-title {
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 22px;
  margin: 0;
  flex: 1;
}
.archive-btn {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.85);
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
}
.archive-btn:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}
.archive-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.tab-bar {
  display: flex;
  gap: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.tab-btn {
  flex: 1;
  padding: 10px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(195, 169, 255, 0.4);
  font-size: 13px;
  font-family: 'Cinzel', serif;
  cursor: pointer;
  transition: all 0.2s;
}
.tab-btn:hover { color: rgba(195, 169, 255, 0.7); }
.tab-btn.active { color: #C9A94E; border-bottom-color: #C9A94E; }

.browse-tab, .simple-tab, .advanced-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Browse */
.breadcrumb { display: flex; align-items: center; gap: 8px; }
.crumb-back {
  background: none;
  border: none;
  color: rgba(195, 169, 255, 0.5);
  font-size: 13px;
  cursor: pointer;
  font-family: inherit;
}
.crumb-back:hover { color: #C9A94E; }
.crumb-trail { font-size: 12px; color: rgba(195, 169, 255, 0.4); }
.crumb-item { cursor: pointer; }
.crumb-item:hover { color: rgba(195, 169, 255, 0.7); }
.filter-pills { display: flex; flex-wrap: wrap; gap: 6px; }
.pill {
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
  text-transform: capitalize;
}
.pill:hover { border-color: rgba(201, 169, 78, 0.4); color: #C9A94E; }
.portrait-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}
.portrait-card {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 1;
  border: 2px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  background: none;
  padding: 0;
  transition: all 0.2s;
}
.portrait-card:hover:not(:disabled) {
  border-color: rgba(201, 169, 78, 0.55);
  transform: scale(1.02);
}
.portrait-card:disabled { cursor: not-allowed; }
.portrait-card.saving {
  border-color: rgba(201, 169, 78, 0.7);
  box-shadow: 0 0 16px rgba(201, 169, 78, 0.25);
}
.portrait-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.card-saving-overlay {
  position: absolute;
  inset: 0;
  background: rgba(8, 0, 15, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #C9A94E;
  font-size: 12px;
  font-style: italic;
  font-family: 'Cinzel', serif;
}
.scroll-sentinel { width: 100%; height: 1px; grid-column: 1 / -1; }
.loading-more {
  text-align: center;
  padding: 12px;
  font-size: 12px;
  color: rgba(201, 169, 78, 0.5);
  font-style: italic;
}
.status-msg {
  text-align: center;
  padding: 32px 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.3);
}

/* Simple */
.known-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(195, 169, 255, 0.16);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: border-color 0.2s, background 0.2s;
}
.known-card.overridden {
  background: rgba(201, 169, 78, 0.08);
  border-color: rgba(201, 169, 78, 0.45);
}
.known-card.overridden .known-label { color: #C9A94E; }
.known-original {
  margin: 6px 0 0;
  font-size: 12px;
  color: rgba(212, 196, 232, 0.45);
  font-style: italic;
  line-height: 1.4;
}
.known-label {
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(212, 196, 232, 0.55);
  font-family: 'Cinzel', serif;
}
.known-summary {
  margin: 0;
  font-size: 14px;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  color: rgba(232, 216, 255, 0.85);
  line-height: 1.5;
}
.tweak-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}
.tweak-label > span {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(212, 196, 232, 0.7);
}
.tweak-label > small {
  font-size: 11px;
  color: rgba(212, 196, 232, 0.45);
  font-style: italic;
}
.tweak-textarea {
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 8px;
  padding: 10px 12px;
  color: #f3e9d2;
  font-family: inherit;
  font-size: 14px;
  outline: none;
  resize: vertical;
  min-height: 70px;
  transition: border-color 0.18s;
}
.tweak-textarea:focus { border-color: #C9A94E; }

/* Advanced (accordion) */
.section-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
  overflow: hidden;
}
.section-card:hover { border-color: rgba(255, 255, 255, 0.12); }
.section-card.active {
  border-color: rgba(201, 169, 78, 0.4);
  box-shadow: 0 0 12px rgba(201, 169, 78, 0.08);
  cursor: default;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
}
.section-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(201, 169, 78, 0.6);
  font-weight: 600;
}
.section-card.active .section-label { color: #C9A94E; }
.section-toggle { font-size: 12px; color: rgba(255, 255, 255, 0.2); }
.section-expanded { padding: 0 14px 12px; }
.section-editable {
  font-size: 14px;
  color: rgba(232, 216, 255, 0.85);
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  line-height: 1.7;
  outline: none;
  min-height: 40px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  border: 1px solid rgba(201, 169, 78, 0.15);
}
.section-editable:focus {
  border-color: rgba(201, 169, 78, 0.3);
  background: rgba(255, 255, 255, 0.04);
}

/* Paint bar */
.paint-bar {
  margin-top: 4px;
  display: flex;
  gap: 8px;
  align-items: stretch;
}
.view-prompt-btn {
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  color: rgba(195, 169, 255, 0.5);
  font-size: 12px;
  cursor: pointer;
  font-family: inherit;
  white-space: nowrap;
  transition: all 0.2s;
}
.view-prompt-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #E8D8FF;
}
.paint-btn {
  flex: 1;
  padding: 16px;
  background: linear-gradient(135deg, rgba(201, 169, 78, 0.15), rgba(201, 169, 78, 0.08));
  border: 1px solid rgba(201, 169, 78, 0.4);
  border-radius: 12px;
  color: #C9A94E;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Cinzel', serif;
  letter-spacing: 0.03em;
  transition: all 0.2s;
}
.paint-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(201, 169, 78, 0.25), rgba(201, 169, 78, 0.12));
  box-shadow: 0 0 20px rgba(201, 169, 78, 0.12);
}
.paint-btn:disabled { opacity: 0.55; cursor: not-allowed; }

/* Prompt Modal */
.prompt-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.prompt-modal {
  background: #1a0a2e;
  border: 1px solid rgba(201, 169, 78, 0.3);
  border-radius: 14px;
  max-width: 500px;
  width: 100%;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
}
.prompt-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 13px;
  color: #C9A94E;
  font-weight: 600;
  font-family: 'Cinzel', serif;
}
.prompt-modal-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
}
.prompt-modal-close:hover { color: #E8D8FF; }
.prompt-modal-body {
  padding: 18px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.7;
  color: rgba(232, 216, 255, 0.8);
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
}

.error {
  color: rgba(244, 67, 54, 0.9);
  font-size: 12px;
  margin: 8px 0 0;
  text-align: center;
}
.rewrite-notice {
  margin: 8px 0 0;
  padding: 10px 14px;
  background: rgba(201, 169, 78, 0.12);
  border: 1px solid rgba(201, 169, 78, 0.4);
  border-radius: 10px;
  font-size: 12px;
  color: #C9A94E;
  text-align: center;
  line-height: 1.5;
}

/* Browse preview modal */
.preview-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.preview-modal-card {
  position: relative;
  background: #1a0c2e;
  border: 1px solid rgba(195, 169, 255, 0.22);
  border-radius: 16px;
  width: 100%;
  max-width: 540px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  padding: 18px 18px 16px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.55);
}
.preview-modal-close {
  position: absolute;
  top: 10px;
  right: 12px;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(195, 169, 255, 0.22);
  color: rgba(212, 196, 232, 0.8);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  font-family: inherit;
  z-index: 1;
}
.preview-modal-close:hover {
  color: #fff;
  border-color: #C9A94E;
}
.preview-modal-image-wrap {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(201, 169, 78, 0.4);
  box-shadow: 0 4px 22px rgba(0, 0, 0, 0.45);
  margin-top: 6px;
}
.preview-modal-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.preview-modal-foot {
  display: flex;
  gap: 10px;
  margin-top: 14px;
  align-items: stretch;
}
.preview-modal-foot .ghost-btn,
.preview-modal-foot .primary-btn {
  flex: 1;
  padding: 11px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-family: 'Cinzel', serif;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
}
.preview-modal-foot .ghost-btn {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.85);
}
.preview-modal-foot .ghost-btn:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}
.preview-modal-foot .primary-btn {
  background: linear-gradient(135deg, rgba(201, 169, 78, 0.95), rgba(201, 169, 78, 0.7));
  border: 1px solid rgba(201, 169, 78, 0.95);
  color: #1a0c2e;
}
.preview-modal-foot .primary-btn:hover:not(:disabled) {
  filter: brightness(1.08);
  box-shadow: 0 0 18px rgba(201, 169, 78, 0.35);
}
.preview-modal-foot button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Archive modal */
.archive-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.74);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.archive-modal-card {
  background: #1a0c2e;
  border: 1px solid rgba(195, 169, 255, 0.22);
  border-radius: 14px;
  width: 100%;
  max-width: 720px;
  max-height: 92vh;
  display: flex;
  flex-direction: column;
  padding: 18px 18px 14px;
  overflow: hidden;
}
.archive-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-shrink: 0;
}
.archive-modal-head h3 {
  margin: 0;
  font-family: 'Cinzel', serif;
  color: #C9A94E;
  font-size: 18px;
}
.archive-modal-close {
  background: transparent;
  border: none;
  color: rgba(212, 196, 232, 0.7);
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 0 6px;
}
.archive-modal-close:hover { color: #fff; }
.archive-empty {
  text-align: center;
  padding: 36px 16px;
  font-size: 13px;
  color: rgba(212, 196, 232, 0.45);
  font-style: italic;
}
.archive-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  overflow-y: auto;
  padding-right: 4px;
  flex: 1;
}
.archive-cell {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 1;
  border: 2px solid rgba(255, 255, 255, 0.08);
  cursor: pointer;
  background: none;
  padding: 0;
  transition: all 0.18s;
}
.archive-cell:hover {
  border-color: rgba(201, 169, 78, 0.55);
  transform: scale(1.02);
}
.archive-cell img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.archive-meta {
  position: absolute;
  bottom: 4px;
  left: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  padding: 3px 6px;
  border-radius: 6px;
  font-size: 10px;
  color: rgba(212, 196, 232, 0.75);
  text-align: center;
  font-family: 'Cinzel', serif;
  letter-spacing: 0.04em;
}
.archive-modal-foot {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  flex-shrink: 0;
}
.ghost-btn-sm {
  background: transparent;
  border: 1px dashed rgba(244, 67, 54, 0.4);
  color: rgba(244, 67, 54, 0.8);
  font-size: 11px;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
}
.ghost-btn-sm:hover {
  background: rgba(244, 67, 54, 0.08);
  border-style: solid;
}
</style>
